import React, { useEffect, useState } from 'react';
import { Bell, X, Settings } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { isPushSupported, getNotificationPermission, subscribeToPush } from '../lib/push';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const FIELD_ROLES = new Set(['capturer', 'territory_manager', 'agent']);
const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

const PushPermissionBanner = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [perm, setPerm] = useState('default');
  const [enabling, setEnabling] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const aboveMobileNav = pathname.startsWith('/app');
  const isField = FIELD_ROLES.has(user?.role);

  useEffect(() => {
    if (!user || !isPushSupported()) return;
    getNotificationPermission().then((p) => {
      setPerm(p);
      if (p === 'granted') {
        // Re-asegurar la suscripción en CADA sesión (los endpoints expiran/rotan)
        subscribeToPush().catch(() => {});
        return;
      }
      // Roles de campo: el banner es PERSISTENTE (reaparece cada sesión) para que
      // activar sea un toque tras pinear la app — sin reinstalar nada.
      if (isField) {
        if (sessionStorage.getItem('pc_push_dismissed_session')) return;
        setTimeout(() => setShow(true), isStandalone() ? 1200 : 4000);
        return;
      }
      // Admin/escritorio: respeta el descarte permanente anterior
      if (localStorage.getItem('pc_push_dismissed')) return;
      if (p === 'default') setTimeout(() => setShow(true), 6000);
    });
  }, [user, isField]);

  const enable = async () => {
    setEnabling(true);
    const r = await subscribeToPush();
    setEnabling(false);
    const p = await getNotificationPermission();
    setPerm(p);
    if (r.ok) {
      toast.success('Notificaciones activadas');
      setShow(false);
    } else if (p === 'denied') {
      setShowHelp(true);
    } else {
      toast.error('No se pudieron activar · intenta de nuevo');
    }
  };

  const dismiss = () => {
    if (isField) sessionStorage.setItem('pc_push_dismissed_session', '1');
    else localStorage.setItem('pc_push_dismissed', '1');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[1100] max-w-[420px] w-[calc(100%-1.5rem)] slide-up"
      style={{ bottom: aboveMobileNav ? 'calc(env(safe-area-inset-bottom, 0px) + 8.25rem)' : '5rem' }}
      data-testid="push-permission-banner"
    >
      <div className="bg-white border border-[#e8e5de] rounded-2xl p-4 shadow-2xl flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#f3f0e9] flex items-center justify-center shrink-0">
          <Bell size={16} className="text-[#1c1c1c]"/>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13.5px] font-medium tracking-tight">
            {perm === 'denied' ? 'Notificaciones bloqueadas' : 'Activa notificaciones'}
          </p>
          {perm === 'denied' ? (
            <>
              <p className="text-[11.5px] text-[#5a5a5a] mt-0.5 leading-relaxed">
                El permiso quedó bloqueado en el navegador. No hace falta reinstalar: actívalo en los ajustes y vuelve aquí.
              </p>
              {showHelp && (
                <div className="mt-2 bg-[#f8f6f1] rounded-lg p-2.5 text-[11px] text-[#3a3a3a] leading-relaxed" data-testid="push-denied-help">
                  <p className="font-medium mb-1 inline-flex items-center gap-1"><Settings size={11}/> Android (Chrome):</p>
                  <p>1. Toca el candado/⋮ junto a la dirección (o mantén presionado el ícono de la app → ⓘ Información).</p>
                  <p>2. Permisos → Notificaciones → <strong>Permitir</strong>.</p>
                  <p className="font-medium mt-1.5 mb-1">iPhone (iOS 16.4+):</p>
                  <p>Ajustes del teléfono → Notificaciones → busca la app pineada → Permitir.</p>
                </div>
              )}
              <div className="flex gap-2 mt-2">
                {!showHelp && (
                  <button onClick={() => setShowHelp(true)} className="px-3 h-7 bg-[#1c1c1c] text-[#f8f6f1] rounded-full text-[11.5px] font-medium" data-testid="push-help-btn">
                    Cómo activarlas
                  </button>
                )}
                <button onClick={enable} disabled={enabling} className="px-3 h-7 border border-[#1c1c1c]/25 rounded-full text-[11.5px] font-medium disabled:opacity-60" data-testid="push-retry-btn">
                  {enabling ? 'Verificando…' : 'Ya las activé'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[11.5px] text-[#5a5a5a] mt-0.5 leading-relaxed">
                {user?.role === 'admin'
                  ? 'Te avisaremos cuando llegue una nueva captura para revisar.'
                  : 'Necesarias para recibir los cambios de precio y avisos de tracking.'}
              </p>
              <button onClick={enable} disabled={enabling} className="mt-2 px-3 h-7 bg-[#1c1c1c] text-[#f8f6f1] rounded-full text-[11.5px] font-medium disabled:opacity-60" data-testid="push-enable-btn">
                {enabling ? 'Activando…' : 'Activar'}
              </button>
            </>
          )}
        </div>
        <button onClick={dismiss} className="text-[#8a8579] hover:text-[#1c1c1c] shrink-0" data-testid="push-dismiss-btn"><X size={16}/></button>
      </div>
    </div>
  );
};

export default PushPermissionBanner;
